/**
 * Plugins Tab Component
 * Single responsibility: display and manage form-scope plugin controls.
 * Knows nothing about field controls.
 */

import React, { useState, useMemo } from 'react';
import { useUIStore } from '@atlas-forms/state-react';
import type { FormSchema, FormControl, ControlPlugin } from '@atlas-forms/types-js';
import { getGlobalReactRegistry } from '@atlas-forms/player-components-react';
import { getPluginConfigSchema } from './pluginConfigSchemas';
import type { PluginConfigField } from './pluginConfigSchemas';
import { pluginConfigSchemaRegistry } from '../../registry/PluginConfigSchemaRegistry';

export interface PluginsTabProps {
  schema: FormSchema;
  onAddPlugin: (control: FormControl) => void;
  onRemovePlugin: (controlId: string) => void;
  onUpdatePluginConfig: (controlId: string, config: Record<string, unknown>) => void;
  apiActions?: any[];
  onUpdateApiActions?: (actions: any[]) => void;
}

/** IDs of active plugin controls whose inline config editor is open */
type OpenConfigSet = Set<string>;

/**
 * Returns at most the first 3 non-empty key/value pairs from a config object,
 * skipping boolean false (not interesting in a summary).
 */
function configSummaryEntries(config: Record<string, unknown>): [string, string][] {
  return Object.entries(config)
    .filter(([, v]) => v !== '' && v !== null && v !== undefined && v !== false && v !== 0)
    .slice(0, 3)
    .map(([k, v]) => [k, String(v)]);
}

// ── Schema-driven field renderer ─────────────────────────────────────────────

interface FieldEditorProps {
  field: PluginConfigField;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  isDark: boolean;
}

const FieldEditor: React.FC<FieldEditorProps> = ({ field, value, onChange, isDark }) => {
  const inputBase: React.CSSProperties = {
    width: '100%',
    padding: '5px 8px',
    borderRadius: 5,
    border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
    background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
    color: isDark ? '#e2e8f0' : '#1e293b',
    fontSize: 12,
    boxSizing: 'border-box' as const,
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
  };

  const labelTextStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: isDark ? '#cbd5e1' : '#475569',
  };

  const descStyle: React.CSSProperties = {
    fontSize: 11,
    color: isDark ? '#64748b' : '#94a3b8',
    marginTop: -2,
  };

  const strVal = value === undefined || value === null ? '' : String(value);
  const boolVal = value === true || value === 'true';
  const numVal = value === undefined || value === null ? '' : String(value);

  switch (field.type) {
    case 'boolean':
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={boolVal}
            onChange={(e) => onChange(field.key, e.target.checked)}
            style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#22c55e' }}
          />
          <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569' }}>
            {field.label}
          </span>
          {field.description && (
            <span style={{ fontSize: 11, color: isDark ? '#64748b' : '#94a3b8' }}>
              — {field.description}
            </span>
          )}
        </label>
      );

    case 'select':
      return (
        <label style={labelStyle}>
          <span style={labelTextStyle}>{field.label}</span>
          {field.description && <span style={descStyle}>{field.description}</span>}
          <select
            value={strVal}
            onChange={(e) => onChange(field.key, e.target.value)}
            style={{ ...inputBase, cursor: 'pointer' }}
          >
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
      );

    case 'number':
      return (
        <label style={labelStyle}>
          <span style={labelTextStyle}>{field.label}</span>
          {field.description && <span style={descStyle}>{field.description}</span>}
          <input
            type="number"
            value={numVal}
            onChange={(e) => onChange(field.key, e.target.valueAsNumber)}
            style={inputBase}
          />
        </label>
      );

    case 'textarea':
      return (
        <label style={labelStyle}>
          <span style={labelTextStyle}>{field.label}</span>
          {field.description && <span style={descStyle}>{field.description}</span>}
          <textarea
            value={strVal}
            onChange={(e) => onChange(field.key, e.target.value)}
            rows={3}
            style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
          />
        </label>
      );

    case 'color':
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="color"
            value={strVal || '#000000'}
            onChange={(e) => onChange(field.key, e.target.value)}
            style={{ width: 32, height: 28, border: 'none', borderRadius: 4, cursor: 'pointer', padding: 2 }}
          />
          <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569' }}>
            {field.label}
          </span>
          <span style={{ fontSize: 11, color: isDark ? '#64748b' : '#94a3b8', fontFamily: 'monospace' }}>
            {strVal || '—'}
          </span>
        </label>
      );

    case 'url':
    case 'text':
    default:
      return (
        <label style={labelStyle}>
          <span style={labelTextStyle}>{field.label}</span>
          {field.description && <span style={descStyle}>{field.description}</span>}
          <input
            type={field.type === 'url' ? 'url' : 'text'}
            value={strVal}
            onChange={(e) => onChange(field.key, e.target.value)}
            style={inputBase}
          />
        </label>
      );
  }
};

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Plugins Tab - Manage form-scope plugin controls
 */
export const PluginsTab: React.FC<PluginsTabProps> = ({
  schema,
  onAddPlugin,
  onRemovePlugin,
  onUpdatePluginConfig,
  apiActions = [],
  onUpdateApiActions,
}) => {
  const { theme } = useUIStore();
  const isDark = theme === 'dark';

  const [openConfigs, setOpenConfigs] = useState<OpenConfigSet>(new Set());
  const [editingActionId, setEditingActionId] = useState<string | null>(null);

  const toggleConfig = (controlId: string) => {
    setOpenConfigs((prev) => {
      const next = new Set(prev);
      next.has(controlId) ? next.delete(controlId) : next.add(controlId);
      return next;
    });
  };

  /** All controls with scope === 'form' */
  const activePlugins = useMemo(
    () => schema.controls.filter((c) => c.scope === 'form'),
    [schema.controls]
  );

  /** All registered plugins whose pluginScope is 'form-only' or 'both' */
  // Registry is a stable singleton — resolved once per mount. If plugins are registered
  // after this component mounts (which does not happen in normal usage), a remount is needed.
  const allRegisteredPlugins = useMemo(
    () =>
      getGlobalReactRegistry()
        .getAllReactPlugins()
        .filter((p) => p.pluginScope === 'form-only' || p.pluginScope === 'both'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /** Active plugin type IDs (for singleton filtering) */
  const activeTypeIds = useMemo(
    () => new Set(activePlugins.map((c) => c.type)),
    [activePlugins]
  );

  /** Available plugin types — exclude singletons already added */
  const availablePlugins = useMemo(
    () =>
      allRegisteredPlugins.filter(
        (p) => !(p.singleton && activeTypeIds.has(p.type))
      ),
    [allRegisteredPlugins, activeTypeIds]
  );

  const getPlugin = (type: string): ControlPlugin | undefined =>
    allRegisteredPlugins.find((p) => p.type === type);

  // ── Styles ───────────────────────────────────────────────────────────────────

  const cardStyle: React.CSSProperties = {
    border: `1px solid ${isDark ? 'rgba(34,197,94,0.25)' : 'rgba(22,163,74,0.2)'}`,
    borderRadius: 8,
    padding: '12px 14px',
    marginBottom: 10,
    background: isDark ? 'rgba(0,0,0,0.2)' : '#ffffff',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: isDark ? '#94a3b8' : '#9ca3af',
    marginBottom: 12,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 600,
    color: isDark ? '#e2e8f0' : '#1e293b',
  };

  const subtextStyle: React.CSSProperties = {
    fontSize: 11,
    color: isDark ? '#64748b' : '#9ca3af',
  };

  const configSummaryStyle: React.CSSProperties = {
    fontSize: 11,
    fontFamily: 'monospace',
    color: isDark ? '#94a3b8' : '#64748b',
    marginTop: 4,
    lineHeight: 1.6,
  };

  const btnBaseStyle: React.CSSProperties = {
    padding: '4px 10px',
    border: 'none',
    borderRadius: 5,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
  };

  const btnConfigStyle: React.CSSProperties = {
    ...btnBaseStyle,
    background: isDark ? 'rgba(51,65,85,0.6)' : '#e2e8f0',
    color: isDark ? '#94a3b8' : '#475569',
  };

  const btnRemoveStyle: React.CSSProperties = {
    ...btnBaseStyle,
    background: '#ef4444',
    color: '#fff',
  };

  const availableBtnStyle: React.CSSProperties = {
    padding: '8px 12px',
    border: `1px dashed ${isDark ? 'rgba(34,197,94,0.35)' : 'rgba(22,163,74,0.35)'}`,
    borderRadius: 7,
    background: isDark ? 'rgba(34,197,94,0.06)' : 'rgba(22,163,74,0.04)',
    color: isDark ? '#22c55e' : '#16a34a',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
    textAlign: 'left' as const,
  };

  const emptyStateStyle: React.CSSProperties = {
    padding: '20px 16px',
    textAlign: 'center' as const,
    color: isDark ? '#94a3b8' : '#9ca3af',
    fontSize: 13,
    border: `1px dashed ${isDark ? 'rgba(71,85,105,0.4)' : '#d1d5db'}`,
    borderRadius: 8,
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '16px 18px' }}>

      {/* ── API Actions Management ────────────────────────────────────────────── */}
      <div style={{ marginBottom: 28 }}>
        <p style={sectionTitleStyle}>Form Actions (API Actions)</p>
        <p style={subtextStyle}>Configure HTTP API calls to external services triggered by form events</p>

        {apiActions.length === 0 ? (
          <button
            type="button"
            style={{
              ...availableBtnStyle,
              width: '100%',
              padding: '16px 12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              justifyContent: 'center',
            }}
            onClick={() => {
              const timestamp = Date.now();
              const random = Math.floor(Math.random() * 10000);
              const newAction = {
                id: `api-action-${timestamp}-${random}`,
                name: `New Action`,
                trigger: 'submit',
                endpoint: '',
                method: 'POST',
                payloadMapping: {},
                headers: {},
                onSuccess: 'none',
                onError: 'none',
              };
              onUpdateApiActions?.([...apiActions, newAction]);
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 600 }}>+ Add Your First API Action</span>
            <span style={{ fontSize: 11, opacity: 0.75, fontWeight: 400 }}>Configure HTTP calls triggered by form events</span>
          </button>
        ) : (
          <>
            {apiActions.map((action, idx) => {
            const isEditing = editingActionId === action.id;

            return (
              <div key={action.id} style={cardStyle}>
                {/* Card header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <span style={labelStyle}>{action.name || `API Action ${idx + 1}`}</span>
                    <span style={{ ...subtextStyle, marginLeft: 8 }}>
                      {action.method} {action.endpoint || '(no endpoint)'}
                    </span>
                    {!isEditing && (
                      <div style={{ ...subtextStyle, marginTop: 6 }}>
                        Trigger: <strong>{action.trigger || 'submit'}</strong>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      type="button"
                      style={btnConfigStyle}
                      onClick={() => setEditingActionId(isEditing ? null : action.id)}
                      aria-label={`${isEditing ? 'Close' : 'Edit'} API action`}
                    >
                      {isEditing ? 'Close' : 'Edit'}
                    </button>
                    <button
                      type="button"
                      style={btnRemoveStyle}
                      onClick={() => {
                        const updated = apiActions.filter(a => a.id !== action.id);
                        onUpdateApiActions?.(updated);
                      }}
                      aria-label="Remove API action"
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {/* Inline editor */}
                {isEditing && (
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                    }}
                  >
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569' }}>
                        Action Name
                      </span>
                      <input
                        type="text"
                        value={action.name || ''}
                        onChange={(e) => {
                          const updated = apiActions.map(a =>
                            a.id === action.id ? { ...a, name: e.target.value } : a
                          );
                          onUpdateApiActions?.(updated);
                        }}
                        placeholder="e.g., Send to API"
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          borderRadius: 5,
                          border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
                          background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          fontSize: 12,
                          boxSizing: 'border-box',
                        }}
                      />
                    </label>

                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569' }}>
                        Endpoint URL
                      </span>
                      <input
                        type="url"
                        value={action.endpoint || ''}
                        onChange={(e) => {
                          const updated = apiActions.map(a =>
                            a.id === action.id ? { ...a, endpoint: e.target.value } : a
                          );
                          onUpdateApiActions?.(updated);
                        }}
                        placeholder="https://api.example.com/endpoint"
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          borderRadius: 5,
                          border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
                          background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          fontSize: 12,
                          boxSizing: 'border-box',
                        }}
                      />
                    </label>

                    <div style={{ display: 'flex', gap: 10 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569' }}>
                          Method
                        </span>
                        <select
                          value={action.method || 'POST'}
                          onChange={(e) => {
                            const updated = apiActions.map(a =>
                              a.id === action.id ? { ...a, method: e.target.value } : a
                            );
                            onUpdateApiActions?.(updated);
                          }}
                          style={{
                            padding: '6px 8px',
                            borderRadius: 5,
                            border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
                            background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: 12,
                            cursor: 'pointer',
                          }}
                        >
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                          <option value="DELETE">DELETE</option>
                        </select>
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569' }}>
                          Trigger Event
                        </span>
                        <select
                          value={action.trigger || 'submit'}
                          onChange={(e) => {
                            const updated = apiActions.map(a =>
                              a.id === action.id ? { ...a, trigger: e.target.value } : a
                            );
                            onUpdateApiActions?.(updated);
                          }}
                          style={{
                            padding: '6px 8px',
                            borderRadius: 5,
                            border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
                            background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: 12,
                            cursor: 'pointer',
                          }}
                        >
                          <option value="submit">On Form Submit</option>
                          <option value="load">On Form Load</option>
                          <option value="field-change">On Field Change</option>
                          <option value="button-click">On Button Click</option>
                          <option value="search">On Search</option>
                        </select>
                      </label>
                    </div>

                    <div style={{ display: 'flex', gap: 10 }}>
                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                        <div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569' }}>
                            On Success
                          </span>
                          <span style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#9ca3af', marginLeft: 8 }}>
                            (what happens when API call succeeds)
                          </span>
                        </div>
                        <select
                          value={action.onSuccess || 'none'}
                          onChange={(e) => {
                            const updated = apiActions.map(a =>
                              a.id === action.id ? { ...a, onSuccess: e.target.value } : a
                            );
                            onUpdateApiActions?.(updated);
                          }}
                          style={{
                            padding: '6px 8px',
                            borderRadius: 5,
                            border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
                            background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: 12,
                            cursor: 'pointer',
                          }}
                        >
                          <option value="none">No Action</option>
                          <option value="notification">Show Notification</option>
                          <option value="redirect">Redirect to URL</option>
                          <option value="refresh">Refresh Form</option>
                        </select>
                      </label>

                      <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                        <div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569' }}>
                            On Error
                          </span>
                          <span style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#9ca3af', marginLeft: 8 }}>
                            (what happens when API call fails)
                          </span>
                        </div>
                        <select
                          value={action.onError || 'none'}
                          onChange={(e) => {
                            const updated = apiActions.map(a =>
                              a.id === action.id ? { ...a, onError: e.target.value } : a
                            );
                            onUpdateApiActions?.(updated);
                          }}
                          style={{
                            padding: '6px 8px',
                            borderRadius: 5,
                            border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
                            background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            fontSize: 12,
                            cursor: 'pointer',
                          }}
                        >
                          <option value="none">No Action</option>
                          <option value="notification">Show Error Message</option>
                          <option value="retry">Retry Request</option>
                          <option value="fallback">Use Fallback Data</option>
                        </select>
                      </label>
                    </div>

                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569' }}>
                        Payload Mapping
                      </span>
                      <span style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#9ca3af' }}>
                        Map form fields to API request body field names (e.g., Email field → user_email in API)
                      </span>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {Object.entries(action.payloadMapping || {}).map(([formField, apiField]) => (
                          <div key={formField} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                            <select
                              value={formField}
                              onChange={(e) => {
                                const newFormField = e.target.value;
                                const payloadMapping = { ...action.payloadMapping };
                                if (formField !== newFormField) {
                                  delete payloadMapping[formField];
                                  payloadMapping[newFormField] = apiField;
                                }
                                const updated = apiActions.map(a =>
                                  a.id === action.id ? { ...a, payloadMapping } : a
                                );
                                onUpdateApiActions?.(updated);
                              }}
                              style={{
                                flex: 0.4,
                                padding: '6px 8px',
                                borderRadius: 4,
                                border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
                                background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
                                color: isDark ? '#e2e8f0' : '#1e293b',
                                fontSize: 12,
                                cursor: 'pointer',
                              }}
                            >
                              <option value="">Select field...</option>
                              {schema.controls?.map((control: any) => (
                                <option key={control.id} value={control.id}>
                                  {control.label || control.id}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={apiField as string}
                              placeholder="API field name"
                              onChange={(e) => {
                                const payloadMapping = { ...action.payloadMapping, [formField]: e.target.value };
                                const updated = apiActions.map(a =>
                                  a.id === action.id ? { ...a, payloadMapping } : a
                                );
                                onUpdateApiActions?.(updated);
                              }}
                              style={{
                                flex: 0.6,
                                padding: '6px 8px',
                                borderRadius: 4,
                                border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
                                background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
                                color: isDark ? '#e2e8f0' : '#1e293b',
                                fontSize: 12,
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const payloadMapping = { ...action.payloadMapping };
                                delete payloadMapping[formField];
                                const updated = apiActions.map(a =>
                                  a.id === action.id ? { ...a, payloadMapping } : a
                                );
                                onUpdateApiActions?.(updated);
                              }}
                              style={{
                                padding: '4px 8px',
                                background: '#ef4444',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const payloadMapping = { ...action.payloadMapping, 'field_id': 'apiFieldName' };
                            const updated = apiActions.map(a =>
                              a.id === action.id ? { ...a, payloadMapping } : a
                            );
                            onUpdateApiActions?.(updated);
                          }}
                          style={{
                            padding: '6px 10px',
                            border: `1px dashed ${isDark ? 'rgba(34,197,94,0.4)' : 'rgba(22,163,74,0.35)'}`,
                            borderRadius: 4,
                            background: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(22,163,74,0.04)',
                            color: isDark ? '#22c55e' : '#16a34a',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            textAlign: 'left',
                          }}
                        >
                          + Add Field Mapping
                        </button>
                      </div>
                    </div>

                    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#cbd5e1' : '#475569' }}>
                        Headers
                      </span>
                      <span style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#9ca3af' }}>
                        Add HTTP headers for authentication or custom API requirements (e.g., Authorization, Content-Type)
                      </span>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {Object.entries(action.headers || {}).map(([key, value]) => (
                          <div key={key} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                            <input
                              type="text"
                              value={key}
                              placeholder="Header name"
                              onChange={(e) => {
                                const newKey = e.target.value;
                                const headers = { ...action.headers };
                                if (key !== newKey) {
                                  delete headers[key];
                                  headers[newKey] = value;
                                }
                                const updated = apiActions.map(a =>
                                  a.id === action.id ? { ...a, headers } : a
                                );
                                onUpdateApiActions?.(updated);
                              }}
                              style={{
                                flex: 0.4,
                                padding: '6px 8px',
                                borderRadius: 4,
                                border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
                                background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
                                color: isDark ? '#e2e8f0' : '#1e293b',
                                fontSize: 12,
                              }}
                            />
                            <input
                              type="text"
                              value={value as string}
                              placeholder="Header value"
                              onChange={(e) => {
                                const headers = { ...action.headers, [key]: e.target.value };
                                const updated = apiActions.map(a =>
                                  a.id === action.id ? { ...a, headers } : a
                                );
                                onUpdateApiActions?.(updated);
                              }}
                              style={{
                                flex: 0.6,
                                padding: '6px 8px',
                                borderRadius: 4,
                                border: `1px solid ${isDark ? 'rgba(71,85,105,0.6)' : '#d1d5db'}`,
                                background: isDark ? 'rgba(15,23,42,0.7)' : '#fff',
                                color: isDark ? '#e2e8f0' : '#1e293b',
                                fontSize: 12,
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const headers = { ...action.headers };
                                delete headers[key];
                                const updated = apiActions.map(a =>
                                  a.id === action.id ? { ...a, headers } : a
                                );
                                onUpdateApiActions?.(updated);
                              }}
                              style={{
                                padding: '4px 8px',
                                background: '#ef4444',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const headers = { ...action.headers, 'X-Custom-Header': 'value' };
                            const updated = apiActions.map(a =>
                              a.id === action.id ? { ...a, headers } : a
                            );
                            onUpdateApiActions?.(updated);
                          }}
                          style={{
                            padding: '6px 10px',
                            border: `1px dashed ${isDark ? 'rgba(34,197,94,0.4)' : 'rgba(22,163,74,0.35)'}`,
                            borderRadius: 4,
                            background: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(22,163,74,0.04)',
                            color: isDark ? '#22c55e' : '#16a34a',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            textAlign: 'left',
                          }}
                        >
                          + Add Header
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

            <button
              type="button"
              style={{
                ...availableBtnStyle,
                width: '100%',
                marginTop: 10,
                textAlign: 'left',
                padding: '10px 12px',
              }}
              onClick={() => {
                const timestamp = Date.now();
                const random = Math.floor(Math.random() * 10000);
                const newAction = {
                  id: `api-action-${timestamp}-${random}`,
                  name: `New Action ${apiActions.length + 1}`,
                  trigger: 'submit',
                  endpoint: '',
                  method: 'POST',
                  payloadMapping: {},
                  headers: {},
                  onSuccess: 'none',
                  onError: 'none',
                };
                onUpdateApiActions?.([...apiActions, newAction]);
              }}
            >
              + Add Another Action
            </button>
          </>
        )}
      </div>

      {/* ── Active Plugins ─────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 28 }}>
        <p style={sectionTitleStyle}>Active Plugins ({activePlugins.length})</p>

        {activePlugins.length === 0 ? (
          <div style={emptyStateStyle}>
            No form plugins active. Add one from the section below.
          </div>
        ) : (
          activePlugins.map((control) => {
            const plugin = getPlugin(control.type);
            const isConfigOpen = openConfigs.has(control.id);
            const config = (control.config ?? {}) as Record<string, unknown>;
            const summary = configSummaryEntries(config);
            // Check PluginConfigSchemaRegistry first (allows dynamic self-registration),
            // then fall back to hardcoded pluginConfigSchemas
            const schema = pluginConfigSchemaRegistry.get(control.type) ?? getPluginConfigSchema(control.type);

            return (
              <div key={control.id} style={cardStyle}>
                {/* Card header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <span style={labelStyle}>{plugin?.label ?? control.type}</span>
                    <span style={{ ...subtextStyle, marginLeft: 8 }}>{control.type}</span>

                    {/* Config summary — non-empty, non-false values */}
                    {summary.length > 0 && (
                      <div style={configSummaryStyle}>
                        {summary.map(([k, v]) => (
                          <div key={k}>{k}: {v}</div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      type="button"
                      style={btnConfigStyle}
                      onClick={() => toggleConfig(control.id)}
                      aria-label={`${isConfigOpen ? 'Close' : 'Configure'} ${plugin?.label ?? control.type}`}
                    >
                      {isConfigOpen ? 'Close' : 'Configure'}
                    </button>
                    <button
                      type="button"
                      style={btnRemoveStyle}
                      onClick={() => onRemovePlugin(control.id)}
                      aria-label={`Remove ${plugin?.label ?? control.type}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {/* Schema-driven inline config editor */}
                {isConfigOpen && (
                  <div style={{
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}>
                    {schema.length === 0 ? (
                      <div style={{ fontSize: 12, color: isDark ? '#475569' : '#9ca3af' }}>
                        This plugin has no configurable options.
                      </div>
                    ) : (
                      schema.map((field) => (
                        <FieldEditor
                          key={field.key}
                          field={field}
                          value={config[field.key] ?? plugin?.defaultConfig?.[field.key]}
                          isDark={isDark}
                          onChange={(key, val) => {
                            onUpdatePluginConfig(control.id, { ...config, [key]: val });
                          }}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* ── Available Plugins ──────────────────────────────────────────────── */}
      <div>
        <p style={sectionTitleStyle}>Available Plugins</p>

        {allRegisteredPlugins.length === 0 ? (
          <div style={{ ...emptyStateStyle, textAlign: 'left' as const }}>
            No form plugins are registered yet. Plugin types will appear here when registered.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
            {availablePlugins.map((plugin) => (
              <button
                key={plugin.type}
                type="button"
                style={availableBtnStyle}
                aria-label={`Add ${plugin.label} plugin`}
                onClick={() =>
                  onAddPlugin({
                    id: `${plugin.type}-${Date.now()}`,
                    type: plugin.type,
                    scope: 'form',
                    order: 9999,
                    config: { ...(plugin.defaultConfig ?? {}) },
                  } as FormControl)
                }
              >
                <div style={{ fontWeight: 600, marginBottom: 2 }}>{plugin.label}</div>
                {plugin.description && (
                  <div style={{ fontSize: 11, opacity: 0.75, fontWeight: 400 }}>{plugin.description}</div>
                )}
                {plugin.singleton && (
                  <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>singleton</div>
                )}
              </button>
            ))}

            {/* All singletons already added */}
            {availablePlugins.length === 0 && allRegisteredPlugins.length > 0 && (
              <div style={{
                gridColumn: '1 / -1',
                padding: '12px 14px',
                fontSize: 13,
                color: isDark ? '#94a3b8' : '#9ca3af',
                border: `1px dashed ${isDark ? 'rgba(71,85,105,0.4)' : '#d1d5db'}`,
                borderRadius: 8,
              }}>
                All available plugins are already active.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

PluginsTab.displayName = 'PluginsTab';
